import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import { useAuth } from '../context/AuthContext';
import { starterGraph } from '../features/productionWorkflow';
import './production.css';

export default function ProductionDepartmentPage() {
  const { projectId, department } = useParams();
  const navigate = useNavigate();
  const { hasTenantPermission } = useAuth();
  const canEdit = hasTenantPermission('project.update');
  const [records, setRecords] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', brief: '' });
  const isCharacter = department === 'character';
  const title = isCharacter ? 'Character' : 'Story';
  useEffect(() => {
    let live = true;
    setRecords(null); setError(''); setCreating(false);
    bakingApi.listProductionRecords(projectId).then(rows => { if (live) setRecords(rows); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [projectId, department]);
  if (!['character', 'story'].includes(department)) return <p>Department not found.</p>;
  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const graph = starterGraph(form.brief);
      if (!isCharacter) {
        graph.nodes[0].label = 'Story brief';
        graph.nodes[1].label = 'Develop the arc';
        graph.nodes[2].label = 'Story outline';
      }
      // Persist only nodes/edges — server rejects unknown graph keys.
      const record = await bakingApi.createProductionRecord(projectId, {
        name: form.name.trim(),
        role: form.role || '',
        brief: form.brief || '',
        department,
        graph: { nodes: graph.nodes, edges: graph.edges },
      });
      navigate(`/console/projects/${projectId}/departments/${department}/${record.id}`);
    } catch (e) {
      const detail =
        e.details && typeof e.details === 'object'
          ? Object.values(e.details)
              .flat()
              .filter(Boolean)
              .join(' ')
          : '';
      setError([e.message, detail].filter(Boolean).join(' '));
      setBusy(false);
    }
  };
  return <div className="production-page">
    <Link to={`/console/projects/${projectId}`}>← Production</Link>
    <header className="production-header"><div><p className="production-eyebrow">DEVELOPMENT WORKSPACE</p><h1>{title} Department</h1><p>{isCharacter ? 'Build the people, creatures, and personalities of your world.' : 'Shape story beats and character arcs into connected material.'}</p></div>{canEdit && <button className="btn btn-dark" onClick={() => setCreating(true)}>+ New {isCharacter ? 'character' : 'story record'}</button>}</header>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {creating && <form className="production-create" onSubmit={create}><h2 className="h4">{isCharacter ? 'Meet a new character' : 'Start a story record'}</h2><p>Start with the essentials. Develop the details in your workflow.</p>
      <label>Name<input autoFocus className="form-control" required maxLength={200} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
      <label>{isCharacter ? 'Role or archetype' : 'Arc or beat'}<input className="form-control" maxLength={200} placeholder={isCharacter ? 'e.g. Reluctant explorer' : 'e.g. First turning point'} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} /></label>
      <label>Brief<textarea className="form-control" rows={3} maxLength={10000} value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} /></label>
      <div className="d-flex gap-2"><button disabled={busy || !form.name.trim()} className="btn btn-dark">{busy ? 'Creating…' : 'Create & open workflow'}</button><button type="button" disabled={busy} className="btn btn-outline-secondary" onClick={() => setCreating(false)}>Cancel</button></div>
    </form>}
    {!records && !error && <p role="status">Loading records…</p>}
    {records && <div className="department-grid">{records.filter(r => r.department === department).map(record => <Link className="production-record-card" key={record.id} to={`/console/projects/${projectId}/departments/${department}/${record.id}`}><span className="production-eyebrow">{record.role || 'IN DEVELOPMENT'}</span><h2>{record.name}</h2><p>{record.brief || 'An idea waiting to take shape.'}</p><strong>Open workflow →</strong></Link>)}</div>}
    {records && !records.some(r => r.department === department) && <div className="production-empty"><span aria-hidden="true">{isCharacter ? '◎' : '↝'}</span><h2>{isCharacter ? 'Every character starts with an idea.' : 'Every story starts somewhere.'}</h2><p>{canEdit ? 'Create your first record to start connecting written material, images, and references.' : 'Your team’s records will appear here.'}</p></div>}
  </div>;
}
